import { useState } from "react";
import { useNavigationEffect } from "../../hooks/useNavigationEffect";
import type { RemoteName } from "../../mfe/mfeConfig";
import type {
  IsRouteEnabledOptions,
  MmRouteConfigMenu,
  MmRouteConfigSubMenu,
  RouteConfig,
  RouteConfigBase,
  RouteConfigSubMenu,
} from "../../navigationConfig";
import type { PartialRequired } from "../../types";
import { userPermissionHasAccess } from "../../utils";
import { useLocation, type CurrentMfe } from "./state";
import type { MfeRouteConfig } from "./types";

/**
 * Shamelessly stolen from:
 * https://github.com/remix-run/react-router/blob/v6.4.0-pre.2/packages/router/utils.ts#L441-L544
 * and made a lot simpler for our use case.
 */
export function matchPath<Path extends string>(
  path: Path,
  pathname: string
): boolean {
  return !!pathname.match(compilePath(path));
}

function compilePath(path: string): RegExp {
  if (path !== "*" && path.endsWith("*") && !path.endsWith("/*")) {
    console.warn(
      `Route path "${path}" will be treated as if it were ` +
        `"${path.replace(/\*$/, "/*")}" because the \`*\` character must ` +
        "always follow a `/` in the pattern. To get rid of this warning, " +
        `please change the route path to "${path.replace(/\*$/, "/*")}".`
    );
  }
  let regexpSource =
    "^" +
    path
      .replace(/\/*\*?$/, "") // Ignore trailing / and /*, we'll handle it below
      .replace(/^\/*/, "/") // Make sure it has a leading /
      .replace(/[\\.*+^${}|()[\]]/g, "\\$&") // Escape special regex chars
      .replace(/\/:(\w+)(\?)?/g, (_: string, _paramName: string, isOptional) =>
        isOptional ? "/?([^\\/]+)?" : "/([^\\/]+)"
      );

  if (path.endsWith("*")) {
    regexpSource +=
      path === "*" || path === "/*"
        ? "(.*)$" // Already matched the initial /, just match the rest
        : "(?:\\/(.+)|\\/*)$"; // Don't include the / in params["*"]
  } else {
    // When matching to the end, ignore trailing slashes
    regexpSource += "\\/*$";
  }

  return new RegExp(regexpSource, "i");
}

export const useMatch = (pattern: string): boolean => {
  const location = useLocation();
  const [isMatching, setIsMatching] = useState(
    matchPath(pattern, location.pathname)
  );

  useNavigationEffect(() => {
    setIsMatching(matchPath(pattern, location.pathname));
  }, []);

  return isMatching;
};

type FallbackRoute = RouteConfigBase & { path: "/*" };
export const getRouterConfig = (
  // Ensures that a route to fallback on is defined
  routeConfig: [...RouteConfigBase[], FallbackRoute],
  isEnabledOption?: IsRouteEnabledOptions
): Record<RemoteName, MfeRouteConfig> => {
  const routableRoutes = getRoutableRoutes(routeConfig, isEnabledOption);

  return routableRoutes.reduce(
    (acc, route): Record<RemoteName, MfeRouteConfig> => {
      const existingEntry = acc[route.mfe.name];

      if (!existingEntry) {
        return {
          ...acc,
          [route.mfe.name]: {
            ...("menu" in route ? { textKey: route.menu.textKey } : {}),
            paths: [route.path],
            import: route.import,
            name: route.mfe.name,
            entry: route.mfe.entry,
          },
        };
      }

      return {
        ...acc,
        [route.mfe.name]: {
          ...existingEntry,
          ...("menu" in route ? { textKey: route.menu.textKey } : {}),
          paths: [...existingEntry.paths, route.path],
        },
      };
    },
    {} as Record<RemoteName, MfeRouteConfig>
  );
};

export const getCheckRoutes =
  (isEnabledOption?: IsRouteEnabledOptions) =>
  (
    route:
      | RouteConfig
      | RouteConfigSubMenu
      | MmRouteConfigMenu
      | MmRouteConfigSubMenu["subRoutes"][0]
  ) =>
    userPermissionHasAccess(
      route.allowedPermissions,
      isEnabledOption?.userRights
    ) &&
    ("isEnabled" in route ? route.isEnabled({ ...isEnabledOption }) : true);

export const getRoutableRoutes = (
  routesConfig: (RouteConfig | RouteConfigSubMenu)[],
  isEnabledOption?: IsRouteEnabledOptions
): RouteConfig[] => {
  const checkRoutes = getCheckRoutes(isEnabledOption);
  const authorizedMainRoutes = routesConfig.filter(checkRoutes);
  const authorizedSubRoutes = authorizedMainRoutes
    .flatMap((x) => {
      if ("menu" in x) {
        if ("subRoutes" in x.menu) return x.menu.subRoutes;
        if ("subRouteSections" in x.menu) {
          const mmRoute = x as unknown as MmRouteConfigMenu;
          return (
            mmRoute?.menu?.subRouteSections?.flatMap(
              (section) => section.subRoutes
            ) || []
          );
        }
      }
      return [];
    })
    .filter(checkRoutes);

  const allRoutes = [...authorizedMainRoutes, ...authorizedSubRoutes];

  return allRoutes.filter(
    (route): route is PartialRequired<typeof route, "mfe" | "key"> =>
      !!route.key && !!route.mfe
  );
};

export const matchPathToMfe = (
  path: string,
  mfesConfig: Record<RemoteName, MfeRouteConfig>
) =>
  Object.values(mfesConfig).reduce<CurrentMfe | null>((previous, mfeConfig) => {
    const matchingPath = mfeConfig.paths.find((mfeRoute) =>
      matchPath(mfeRoute, path)
    );
    if (matchingPath) {
      /**
       * We assume that given a path `/foo/bar`, a matching MFE with
       * `/foo/bar` is more favorable than a MFE with just `/foo/*`, but if
       * there was a previous match on `/`, we don't want the fallback `/*`
       * to override it.
       **/
      const isFallback = matchingPath === "/*";
      if (
        !previous ||
        (matchingPath.length > previous.path.length && !isFallback)
      ) {
        return { ...mfeConfig, path: matchingPath };
      }
    }

    return previous;
  }, null);
